import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useUnsavedChanges } from "@/components/UnsavedChangesGuard";
import { canAccessModule, canAccessSection } from "@/utils/permissions";
import axios from "axios";
import {
  Settings, LogOut, Plane, User, Users, Crown, Eye, Edit, Building2, UserPlus,
  BookUser, Table2, MapPin, Hotel, Package, Car, Globe, FileText, ClipboardList,
  ShoppingCart, Briefcase, Link2, Calculator, Map, Ticket, Mail, Home,
  TrendingUp, KeyRound, Zap, ClipboardCheck, FilePlus, Lock,
  BadgeDollarSign, LayoutDashboard, Receipt, FlaskConical, Clock, Wallet, Coins,
  ChevronLeft, ChevronRight, MailCheck, Contact2,
} from "lucide-react";
import {
  Menubar, MenubarMenu, MenubarTrigger, MenubarContent, MenubarItem,
  MenubarSeparator, MenubarSub, MenubarSubTrigger, MenubarSubContent,
} from "@/components/ui/menubar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import CalcQuick from "@/components/CalcQuick";
import SimpleCalc from "@/components/SimpleCalc";
import QuickCalendar from "@/components/QuickCalendar";

const roleLabels = {
  admin: { label: "Admin", icon: Crown, color: "bg-amber-100 text-amber-700" },
  supervisor: { label: "Supervisor", icon: Eye, color: "bg-blue-100 text-blue-700" },
  ejecutivo: { label: "Ejecutivo", icon: Edit, color: "bg-slate-200 text-slate-600" },
};

const triggerCls = "relative z-[1] px-3.5 py-1.5 text-[13px] font-semibold text-cyan-50 rounded-full cursor-pointer select-none bg-transparent border border-transparent hover:bg-transparent hover:text-white focus:bg-transparent focus:text-white data-[state=open]:bg-transparent data-[state=open]:text-white transition-[color,transform] duration-150 will-change-transform origin-center";
const contentCls = "pd-glass min-w-[230px] rounded-2xl p-1.5 text-slate-800";
const itemCls = "gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium text-slate-800 cursor-pointer focus:bg-[#0037ff] focus:text-white data-[state=open]:bg-[#0037ff] data-[state=open]:text-white [&_svg]:focus:text-white";

const NoAccess = () => (
  <div className="px-4 py-6 flex flex-col items-center gap-2 text-center" data-testid="menu-no-access">
    <span className="w-9 h-9 rounded-full bg-slate-500/15 flex items-center justify-center"><Lock className="text-slate-600" style={{ width: 18, height: 18 }} /></span>
    <p className="text-[13px] font-semibold text-slate-800">No tienes acceso a este módulo</p>
    <p className="text-[11px] text-slate-500">Solicita el acceso a un administrador.</p>
  </div>
);

const Sub = ({ label, icon: Icon, testid, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <MenubarSub open={open} onOpenChange={setOpen}>
      <MenubarSubTrigger className={itemCls} onClick={() => setOpen(true)} onPointerDown={() => setOpen(true)} data-testid={testid}>
        {Icon && <Icon className="w-4 h-4 text-slate-500 mr-2.5" />} {label}
      </MenubarSubTrigger>
      <MenubarSubContent className={`${contentCls} max-h-[70vh] overflow-y-auto`} sideOffset={8} alignOffset={-4}>
        {children}
      </MenubarSubContent>
    </MenubarSub>
  );
};

const TopMenuBar = () => {
  const { user, logout } = useAuth();
  const { checkNavigation } = useUnsavedChanges();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [tipoCambio, setTipoCambio] = useState(null);
  const [gmailStatus, setGmailStatus] = useState({ connected: false });
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try { setSettings((await axios.get(`${API}/settings`, { withCredentials: true })).data); } catch {}
    };
    const fetchTC = async () => {
      try { setTipoCambio((await axios.get(`${API}/tipo-cambio`)).data); } catch {}
    };
    const fetchGmail = async () => {
      try { setGmailStatus((await axios.get(`${API}/oauth/gmail/status`, { withCredentials: true })).data); } catch {}
    };
    fetchSettings(); fetchTC(); fetchGmail();

    // Refrescar cuando cambien los incrementos de moneda desde Configuración
    const onTcUpdated = () => fetchTC();
    window.addEventListener("tc-updated", onTcUpdated);

    const tcInterval = setInterval(fetchTC, 60 * 60 * 1000);
    let tcTimeout;
    const msHasta1201MX = () => {
      const mxNow = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Mexico_City" }));
      const target = new Date(mxNow);
      target.setHours(12, 1, 30, 0);
      if (mxNow >= target) target.setDate(target.getDate() + 1);
      return target - mxNow;
    };
    const arm1201 = () => { tcTimeout = setTimeout(() => { fetchTC(); arm1201(); }, msHasta1201MX()); };
    arm1201();

    const handleGmailMessage = (e) => {
      if (e.data?.type === "gmail_connected") setGmailStatus({ connected: true, email: e.data.email });
    };
    window.addEventListener("message", handleGmailMessage);
    return () => {
      window.removeEventListener("message", handleGmailMessage);
      window.removeEventListener("tc-updated", onTcUpdated);
      clearInterval(tcInterval);
      clearTimeout(tcTimeout);
    };
  }, []);

  const hasAccess = (k) => canAccessModule(user, k);
  const hasSectionAccess = (k) => canAccessSection(user, k);

  const go = (to) => {
    if (checkNavigation(to)) navigate(to);
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const connectGmail = async () => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/authorize`, { withCredentials: true });
      window.open(res.data.auth_url, "gmail_auth", "width=500,height=700");
    } catch (err) { console.error("Error connecting Gmail:", err); }
  };

  const disconnectGmail = async () => {
    try {
      await axios.delete(`${API}/oauth/gmail/disconnect`, { withCredentials: true });
      setGmailStatus({ connected: false });
    } catch (err) { console.error("Error disconnecting Gmail:", err); }
  };

  const RoleIcon = roleLabels[user?.role]?.icon || Edit;

  // Cápsula gota de agua: el borde delantero llega rápido y el trasero se despega
  // gradualmente del menú anterior (estiramiento sutil, sin saltos)
  const dockRef = useRef(null);
  const pillRef = useRef(null);
  const movePill = (el, scale) => {
    const pill = pillRef.current, wrap = dockRef.current;
    if (!pill || !wrap) return;
    const wr = wrap.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const cx = r.left + r.width / 2 - wr.left;
    const cy = r.top + r.height / 2 - wr.top;
    const tLeft = cx - el.offsetWidth / 2;
    const tRight = wr.width - (tLeft + el.offsetWidth);
    const tTop = cy - el.offsetHeight / 2;
    const key = el.dataset.testid || el.textContent;
    pill.style.height = `${el.offsetHeight}px`;
    pill.style.top = `${tTop}px`;
    pill.style.transform = `scale(${scale})`;
    pill.style.width = "auto";
    if (pill.dataset.on !== "1") {
      pill.style.transition = "opacity 140ms ease-out";
      pill.style.left = `${tLeft}px`;
      pill.style.right = `${tRight}px`;
      pill.style.opacity = "1";
      pill.dataset.on = "1";
      pill.dataset.target = key;
      return;
    }
    if (pill.dataset.target === key) return;
    pill.dataset.target = key;
    const haciaDerecha = tLeft >= (parseFloat(pill.style.left) || tLeft);
    const rapida = "220ms cubic-bezier(.3,1,.4,1)";
    const lenta = "430ms cubic-bezier(.3,.7,.3,1)";
    pill.style.transition = `left ${haciaDerecha ? lenta : rapida}, right ${haciaDerecha ? rapida : lenta}, top 200ms ease-out, transform 150ms ease-out, opacity 140ms ease-out`;
    pill.style.left = `${tLeft}px`;
    pill.style.right = `${tRight}px`;
    pill.style.opacity = "1";
  };
  const hidePill = () => {
    const pill = pillRef.current;
    if (pill) { pill.style.opacity = "0"; pill.dataset.on = "0"; pill.dataset.target = ""; }
  };
  const dockReset = () => {
    dockRef.current?.querySelectorAll("[data-dock-item]").forEach((el) => {
      el.style.transform = "scale(1)";
    });
  };
  const dockMove = (e) => {
    const wrap = dockRef.current;
    if (!wrap) return;
    const abierto = wrap.querySelector('[data-dock-item][data-state="open"]');
    if (abierto) { dockReset(); movePill(abierto, 1); return; }
    let hovered = null;
    wrap.querySelectorAll("[data-dock-item]").forEach((el) => {
      const r = el.getBoundingClientRect();
      const dentro = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      el.style.transform = dentro ? "scale(1.18)" : "scale(1)";
      if (dentro) hovered = el;
    });
    if (hovered) movePill(hovered, 1.18);
  };
  const dockLeave = () => { dockReset(); hidePill(); };

  // Scroll horizontal del menú con flechitas cuando no cabe en la pantalla
  const scrollRef = useRef(null);
  const [scrollState, setScrollState] = useState({ left: false, right: false });
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    const canLeft = el.scrollLeft > 2;
    const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setScrollState((s) => (s.left === canLeft && s.right === canRight ? s : { left: canLeft, right: canRight }));
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);
  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(160, el.clientWidth * 0.6), behavior: "smooth" });
  };

  // Menú móvil: secciones planas con permisos
  const seccionesMobile = [
    { label: "Clientes", key: "clientes", items: [
      hasAccess("cabina_expedientes") && { to: "/cabina-ventas", icon: LayoutDashboard, label: "Ventas · Cabina de Ventas" },
      hasAccess("clientes_nuevo") && { to: "/clientes/nuevo", icon: UserPlus, label: "Nuevo Cliente" },
      hasAccess("clientes_crm") && { to: "/clientes/crm", icon: BookUser, label: "Clientes" },
      hasAccess("clientes_encuestas") && { to: "/clientes/encuestas", icon: ClipboardCheck, label: "Encuestas de Calidad" },
      hasAccess("clientes_directorio_contactos") && { to: "/clientes/directorio-contactos", icon: Contact2, label: "Directorio de Contactos" },
    ].filter(Boolean) },
    { label: "Comercial", key: "cotizaciones", items: [
      hasAccess("cotizaciones_nueva") && { to: "/nueva-cotizacion", icon: FilePlus, label: "Nueva Cotización" },
      hasAccess("cotizaciones_historial") && { to: "/historial", icon: FileText, label: "Cotizaciones Realizadas" },
      hasAccess("cotizaciones_tabla") && { to: "/comercial/tabla-cotizadora", icon: Table2, label: "Tabla Cotizadora" },
    ].filter(Boolean) },
    { label: "Traveldesk", key: "traveldesk", items: [
      hasAccess("traveldesk_calculadora") && { to: "/traveldesk/calculadora", icon: Calculator, label: "Calculadora de Precios" },
      hasAccess("traveldesk_itinerarios") && { to: "/traveldesk/itinerarios", icon: Map, label: "Itinerarios" },
      hasAccess("traveldesk_perfil_viajero") && { to: "/traveldesk/perfil-viajero/interno", icon: Users, label: "Viajero Interno" },
      hasAccess("traveldesk_perfil_viajero_externo") && { to: "/traveldesk/perfil-viajero/externo", icon: Users, label: "Viajero Externo" },
      hasAccess("traveldesk_formularios_btravel") && { to: "/formularios-clientes/servicios", icon: ClipboardList, label: "Formularios BTravel · Cotizaciones" },
      hasAccess("traveldesk_formularios_btravel") && hasAccess("clientes_formularios_accesos") && { to: "/formularios-clientes/accesos", icon: KeyRound, label: "Formularios BTravel · Accesos" },
      hasAccess("traveldesk_formularios_btravel") && hasAccess("traveldesk_formularios_btravel_consola") && { to: "/formularios-clientes/consola", icon: MailCheck, label: "Formularios BTravel · Consola" },
      hasAccess("traveldesk_dashboard_servicios") && { to: "/traveldesk/dashboard-servicios", icon: LayoutDashboard, label: "Dashboard de Servicios" },
    ].filter(Boolean) },
    { label: "Helpdesk", key: "helpdesk", items: [
      hasAccess("helpdesk_tickets") && { to: "/helpdesk/tickets", icon: Ticket, label: "Tickets" },
      hasAccess("helpdesk_contactos") && { to: "/helpdesk/contactos", icon: Users, label: "Contactos" },
      hasAccess("helpdesk_metricas") && { to: "/helpdesk/metricas", icon: TrendingUp, label: "Métricas" },
      hasAccess("helpdesk_quick_replies") && { to: "/helpdesk/respuestas-rapidas", icon: Zap, label: "Respuestas rápidas" },
    ].filter(Boolean) },
    { label: "Logística", key: "logistica", items: [
      hasAccess("logistica_productos") && { to: "/logistica/productos", icon: Package, label: "Productos" },
      hasAccess("logistica_inventarios") && { to: "/logistica/inventarios", icon: ClipboardList, label: "Inventarios" },
      hasAccess("logistica_pedidos") && { to: "/logistica/pedidos", icon: ShoppingCart, label: "Pedidos" },
      hasAccess("logistica_vehiculos") && { to: "/logistica/vehiculos", icon: Car, label: "Vehículos" },
    ].filter(Boolean) },
    { label: "Administración", key: "administracion", items: [
      hasAccess("activos_fijos") && { to: "/activos-fijos", icon: Briefcase, label: "Activos Fijos" },
      hasAccess("erp_facturacion") && { to: "/erp/facturacion", icon: FileText, label: "ERP · Facturación" },
      hasAccess("erp_vinculacion") && { to: "/erp/vinculacion", icon: Link2, label: "ERP · Vinculación Clientes" },
      hasAccess("cabina_costos") && { to: "/admin/ventas/tablero-costos", icon: LayoutDashboard, label: "Ventas · Tablero de Costos" },
      hasAccess("cabina_matriz_fiscal") && { to: "/admin/ventas/matriz-fiscal", icon: Receipt, label: "Ventas · Matriz Fiscal" },
      hasAccess("admin_facturacion_global") && { to: "/admin/facturacion-global", icon: FileText, label: "Ventas · Facturación · Facturas Globales" },
      hasAccess("admin_pagos_globales") && { to: "/admin/pagos-globales", icon: Wallet, label: "Ventas · Facturación · Pagos Globales" },
      hasAccess("admin_ventas_pendientes") && { to: "/admin/ventas/pendientes", icon: Clock, label: "Ventas · Pendientes aprobación" },
      hasAccess("admin_monedas_config") && { to: "/admin/monedas/configuracion", icon: Coins, label: "Monedas · Configuración" },
      hasAccess("admin_monedas_tc") && { to: "/admin/monedas/tipos-cambio", icon: TrendingUp, label: "Monedas · Tipos de Cambio" },
    ].filter(Boolean) },
    { label: "Configuración", key: "configuracion_section", items: [
      hasAccess("config_agencia") && { to: "/configuracion/agencia", icon: Building2, label: "Datos de la Agencia" },
      hasAccess("config_opciones") && { to: "/configuracion/opciones", icon: Settings, label: "Opciones" },
      hasAccess("config_paginas_publicas") && { to: "/configuracion/paginas-publicas", icon: Globe, label: "Páginas públicas" },
      hasAccess("config_usuarios") && { to: "/usuarios", icon: Users, label: "Usuarios" },
      hasAccess("cabina_laboratorio") && { to: "/configuracion/ventas/laboratorio", icon: FlaskConical, label: "Ventas · Laboratorio" },
      hasAccess("config_tablas_aeropuertos") && { to: "/configuracion/tablas/aeropuertos", icon: Plane, label: "Tablas · Aeropuertos" },
      hasAccess("config_tablas_destinos") && { to: "/configuracion/tablas/destinos", icon: MapPin, label: "Tablas · Destinos" },
      hasAccess("config_tablas_aerolineas") && { to: "/configuracion/tablas/aerolineas", icon: Plane, label: "Tablas · Aerolíneas" },
      hasAccess("config_tablas_hoteles") && { to: "/configuracion/tablas/hoteles", icon: Hotel, label: "Tablas · Hoteles" },
      hasAccess("config_tablas_proveedores") && { to: "/configuracion/tablas/proveedores", icon: Package, label: "Tablas · Proveedores" },
      hasAccess("config_tablas_articulos") && { to: "/configuracion/tablas/articulos", icon: Package, label: "Tablas · Artículos" },
      hasAccess("config_tablas_centros") && { to: "/configuracion/tablas/centros-trabajo", icon: Building2, label: "Tablas · Centros de Trabajo" },
      hasAccess("config_tablas_empleados") && { to: "/configuracion/tablas/empleados", icon: Users, label: "Tablas · Empleados" },
      hasAccess("config_tablas_departamentos") && { to: "/configuracion/tablas/departamentos-operativos", icon: Building2, label: "Tablas · Departamentos" },
      hasAccess("config_tablas_categorias_tkt") && { to: "/configuracion/tablas/categorias-tickets", icon: Ticket, label: "Tablas · Categorías Tickets" },
    ].filter(Boolean) },
  ];
  const goMobile = (to) => { setMobileOpen(false); go(to); };

  const MI = ({ to, icon: Icon, children, testid }) => (
    <MenubarItem className={`${itemCls} ${location.pathname === to ? "bg-[#0037ff]/15 text-[#0037ff] font-semibold" : ""}`} onSelect={() => go(to)} data-testid={testid}>
      {Icon && <Icon className="w-4 h-4 text-slate-500 group-focus:text-white" />} {children}
    </MenubarItem>
  );

  // Menú de módulo: siempre visible; sin acceso muestra panel bloqueado
  const ModuleMenu = ({ label, sectionKey, testid, children }) => (
    <MenubarMenu>
      <MenubarTrigger className={triggerCls} data-dock-item="true" data-testid={testid}>{label}</MenubarTrigger>
      <MenubarContent className={contentCls} align="start" sideOffset={8}>
        {sectionKey && !hasSectionAccess(sectionKey) ? <NoAccess /> : children}
      </MenubarContent>
    </MenubarMenu>
  );

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 shadow-md"
      style={{ background: "linear-gradient(90deg, #00157f 0%, #0037ff 100%)" }}
      data-testid="top-menubar"
    >
      <div className="flex items-center h-12 px-3 gap-2">
        {/* Hamburguesa (móvil/tablet) */}
        <button onClick={() => setMobileOpen(true)} className="md:hidden text-white p-1.5 rounded-md hover:bg-white/15" data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </button>

        {/* Logo */}
        <button onClick={() => go("/")} className="flex items-center gap-2 pr-2 shrink-0" data-testid="menubar-logo">
          {settings?.sidebar_logo_url ? (
            <img src={settings.sidebar_logo_url} alt="Logo" className="h-7 object-contain" onError={(e) => (e.target.style.display = "none")} />
          ) : (
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#04ccda" }}>
              <Plane className="w-4 h-4 text-white" />
            </div>
          )}
          <span className="text-white font-bold text-sm tracking-tight hidden md:block" style={{ fontFamily: "'Mulish', sans-serif" }}>
            {settings?.name || "PD Travel"}
          </span>
        </button>

        {/* Menús — capa exterior overflow-visible (para que la cápsula/hover no se corte),
            interior scroll horizontal */}
        <div ref={dockRef} onMouseMove={dockMove} onMouseLeave={dockLeave} onClick={() => setTimeout(dockReset, 50)} className="hidden md:block flex-1 relative min-w-0" style={{ overflow: "visible" }}>
          <div ref={pillRef} className="pd-pill-slider" />
          {/* Flecha izquierda cuando hay contenido oculto */}
          {scrollState.left && (
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-8 w-7 flex items-center justify-center text-white/95 hover:text-white bg-gradient-to-r from-[#0037ff] via-[#0037ff]/95 to-transparent rounded-l-md"
              aria-label="Desplazar menú a la izquierda"
              data-testid="menubar-scroll-left"
            >
              <ChevronLeft className="w-5 h-5 drop-shadow" />
            </button>
          )}
          {/* Flecha derecha cuando hay contenido oculto */}
          {scrollState.right && (
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-8 w-7 flex items-center justify-center text-white/95 hover:text-white bg-gradient-to-l from-[#0037ff] via-[#0037ff]/95 to-transparent rounded-r-md"
              aria-label="Desplazar menú a la derecha"
              data-testid="menubar-scroll-right"
            >
              <ChevronRight className="w-5 h-5 drop-shadow" />
            </button>
          )}
          <div ref={scrollRef} className="min-w-0 overflow-x-auto pd-menubar-scroll" style={{ paddingTop: 2, paddingBottom: 2 }}>
          <Menubar className="border-0 bg-transparent h-auto p-0 gap-0.5 flex-nowrap whitespace-nowrap" style={{ fontFamily: "'Mulish', sans-serif" }}>
          <MenubarMenu>
            <MenubarTrigger className={triggerCls} data-dock-item="true" data-testid="nav-inicio">Inicio</MenubarTrigger>
            <MenubarContent className={contentCls} align="start" sideOffset={8}>
              <MI to="/" icon={Home} testid="nav-inicio-home">Página de inicio</MI>
              {hasAccess("comercial_dashboard") && <MI to="/dashboard-comercial" icon={TrendingUp} testid="nav-dashboard-comercial">Dashboard Comercial</MI>}
            </MenubarContent>
          </MenubarMenu>

          <ModuleMenu label="Clientes" sectionKey="clientes" testid="nav-clientes">
            {hasAccess("cabina_expedientes") && (
              <Sub label="Ventas" icon={BadgeDollarSign} testid="nav-clientes-ventas">
                <MI to="/cabina-ventas" icon={LayoutDashboard} testid="nav-cabina-ventas">Cabina de Ventas</MI>
              </Sub>
            )}
            {hasAccess("clientes_nuevo") && <MI to="/clientes/nuevo" icon={UserPlus} testid="nav-nuevo-cliente">Nuevo Cliente</MI>}
            {hasAccess("clientes_crm") && <MI to="/clientes/crm" icon={BookUser} testid="nav-crm">Clientes</MI>}
            {hasAccess("clientes_encuestas") && <MI to="/clientes/encuestas" icon={ClipboardCheck} testid="nav-encuestas-calidad">Encuestas de Calidad</MI>}
            {hasAccess("clientes_directorio_contactos") && <MI to="/clientes/directorio-contactos" icon={Contact2} testid="nav-directorio-contactos">Directorio de Contactos</MI>}
          </ModuleMenu>

          <ModuleMenu label="Comercial" sectionKey="cotizaciones" testid="nav-comercial">
            {hasAccess("cotizaciones_nueva") && <MI to="/nueva-cotizacion" icon={FilePlus} testid="nav-nueva-cotizacion">Nueva Cotización</MI>}
            {hasAccess("cotizaciones_historial") && <MI to="/historial" icon={FileText} testid="nav-cotizaciones-realizadas">Cotizaciones Realizadas</MI>}
            {hasAccess("cotizaciones_tabla") && <MI to="/comercial/tabla-cotizadora" icon={Table2} testid="nav-tabla-cotizadora">Tabla Cotizadora</MI>}
          </ModuleMenu>

          <ModuleMenu label="Traveldesk" sectionKey="traveldesk" testid="nav-traveldesk">
            {hasAccess("traveldesk_calculadora") && <MI to="/traveldesk/calculadora" icon={Calculator} testid="nav-traveldesk-calculadora">Calculadora de Precios</MI>}
            {hasAccess("traveldesk_itinerarios") && <MI to="/traveldesk/itinerarios" icon={Map} testid="nav-traveldesk-itinerarios">Itinerarios</MI>}
            {(hasAccess("traveldesk_perfil_viajero") || hasAccess("traveldesk_perfil_viajero_externo")) && (
              <Sub label="Perfil de Viajero" icon={BookUser} testid="nav-traveldesk-perfil-viajero">
                {hasAccess("traveldesk_perfil_viajero") && <MI to="/traveldesk/perfil-viajero/interno" icon={Users} testid="nav-traveldesk-viajero-interno">Viajero Interno</MI>}
                {hasAccess("traveldesk_perfil_viajero_externo") && <MI to="/traveldesk/perfil-viajero/externo" icon={Users} testid="nav-traveldesk-viajero-externo">Viajero Externo</MI>}
              </Sub>
            )}
            {hasAccess("traveldesk_formularios_btravel") && (
              <Sub label="Formularios BTravel" icon={ClipboardList} testid="nav-formularios-btravel">
                <MI to="/formularios-clientes/servicios" icon={ClipboardList} testid="nav-formularios-cotizaciones">Formulario Cotizaciones</MI>
                {hasAccess("clientes_formularios_accesos") && <MI to="/formularios-clientes/accesos" icon={KeyRound} testid="nav-formularios-accesos">Administrar accesos</MI>}
                {hasAccess("traveldesk_formularios_btravel_consola") && <MI to="/formularios-clientes/consola" icon={MailCheck} testid="nav-formularios-consola">Consola de Solicitudes</MI>}
              </Sub>
            )}
            {hasAccess("traveldesk_dashboard_servicios") && <MI to="/traveldesk/dashboard-servicios" icon={LayoutDashboard} testid="nav-traveldesk-dashboard-servicios">Dashboard de Servicios</MI>}
          </ModuleMenu>

          <ModuleMenu label="Hospitality" sectionKey="hospitality" testid="nav-hospitality">
            <Sub label="Perfil de Viajero" icon={BookUser} testid="nav-hospitality-perfil-viajero">
              {(hasAccess("hospitality_perfil_viajero") || hasAccess("traveldesk_perfil_viajero")) && <MI to="/traveldesk/perfil-viajero/interno" icon={Users} testid="nav-hospitality-viajero-interno">Viajero Interno</MI>}
              {(hasAccess("hospitality_perfil_viajero_externo") || hasAccess("traveldesk_perfil_viajero_externo")) && <MI to="/traveldesk/perfil-viajero/externo" icon={Users} testid="nav-hospitality-viajero-externo">Viajero Externo</MI>}
            </Sub>
          </ModuleMenu>

          <ModuleMenu label="Helpdesk" sectionKey="helpdesk" testid="nav-helpdesk">
            {hasAccess("helpdesk_tickets") && <MI to="/helpdesk/tickets" icon={Ticket} testid="nav-helpdesk-tickets">Tickets</MI>}
            {hasAccess("helpdesk_contactos") && <MI to="/helpdesk/contactos" icon={Users} testid="nav-helpdesk-contactos">Contactos</MI>}
            {hasAccess("helpdesk_metricas") && <MI to="/helpdesk/metricas" icon={TrendingUp} testid="nav-helpdesk-metricas">Métricas</MI>}
            {hasAccess("helpdesk_quick_replies") && <MI to="/helpdesk/respuestas-rapidas" icon={Zap} testid="nav-helpdesk-quick-replies">Respuestas rápidas</MI>}
          </ModuleMenu>

          <ModuleMenu label="Logística" sectionKey="logistica" testid="nav-logistica">
            {hasAccess("logistica_productos") && <MI to="/logistica/productos" icon={Package} testid="nav-logistica-productos">Productos</MI>}
            {hasAccess("logistica_inventarios") && <MI to="/logistica/inventarios" icon={ClipboardList} testid="nav-logistica-inventarios">Inventarios</MI>}
            {hasAccess("logistica_pedidos") && <MI to="/logistica/pedidos" icon={ShoppingCart} testid="nav-logistica-pedidos">Pedidos</MI>}
            {hasAccess("logistica_vehiculos") && <MI to="/logistica/vehiculos" icon={Car} testid="nav-logistica-vehiculos">Vehículos</MI>}
          </ModuleMenu>

          <ModuleMenu label="Administración" sectionKey="administracion" testid="nav-administracion">
            {hasAccess("activos_fijos") && <MI to="/activos-fijos" icon={Briefcase} testid="nav-activos-fijos">Activos Fijos</MI>}
            {(hasAccess("erp_facturacion") || hasAccess("erp_vinculacion")) && (
              <Sub label="ERP" icon={Globe} testid="nav-erp">
                {hasAccess("erp_facturacion") && <MI to="/erp/facturacion" icon={FileText} testid="nav-erp-facturacion">Facturación</MI>}
                {hasAccess("erp_vinculacion") && <MI to="/erp/vinculacion" icon={Link2} testid="nav-erp-vinculacion">Vinculación Clientes</MI>}
              </Sub>
            )}
            {(hasAccess("cabina_costos") || hasAccess("cabina_matriz_fiscal") || hasAccess("admin_facturacion_global") || hasAccess("admin_pagos_globales") || hasAccess("admin_ventas_pendientes")) && (
              <Sub label="Ventas" icon={BadgeDollarSign} testid="nav-admin-ventas">
                {hasAccess("cabina_costos") && <MI to="/admin/ventas/tablero-costos" icon={LayoutDashboard} testid="nav-cabina-tablero-costos">Tablero de Costos</MI>}
                {hasAccess("cabina_matriz_fiscal") && <MI to="/admin/ventas/matriz-fiscal" icon={Receipt} testid="nav-cabina-matriz-fiscal">Matriz Fiscal</MI>}
                {(hasAccess("admin_facturacion_global") || hasAccess("admin_pagos_globales")) && (
                  <Sub label="Facturación" icon={FileText} testid="nav-admin-ventas-facturacion">
                    {hasAccess("admin_facturacion_global") && <MI to="/admin/facturacion-global" icon={FileText} testid="nav-facturacion-global">Facturas Globales</MI>}
                    {hasAccess("admin_pagos_globales") && <MI to="/admin/pagos-globales" icon={Wallet} testid="nav-pagos-globales">Pagos Globales</MI>}
                  </Sub>
                )}
                {hasAccess("admin_ventas_pendientes") && <MI to="/admin/ventas/pendientes" icon={Clock} testid="nav-cabina-pendientes">Pendientes de aprobación</MI>}
              </Sub>
            )}
            {(hasAccess("admin_monedas_config") || hasAccess("admin_monedas_tc")) && (
              <Sub label="Monedas" icon={Coins} testid="nav-admin-monedas">
                {hasAccess("admin_monedas_config") && <MI to="/admin/monedas/configuracion" icon={Settings} testid="nav-monedas-config">Configuración</MI>}
                {hasAccess("admin_monedas_tc") && <MI to="/admin/monedas/tipos-cambio" icon={TrendingUp} testid="nav-monedas-tc">Tipos de Cambio</MI>}
              </Sub>
            )}
          </ModuleMenu>

          <ModuleMenu label="Configuración" sectionKey="configuracion_section" testid="nav-configuracion">
            {hasAccess("config_agencia") && <MI to="/configuracion/agencia" icon={Building2} testid="nav-config-agencia">Datos de la Agencia</MI>}
            {hasAccess("config_opciones") && <MI to="/configuracion/opciones" icon={Settings} testid="nav-config-opciones">Opciones</MI>}
            {hasAccess("config_paginas_publicas") && <MI to="/configuracion/paginas-publicas" icon={Globe} testid="nav-config-paginas-publicas">Páginas públicas</MI>}
            {hasAccess("config_usuarios") && <MI to="/usuarios" icon={Users} testid="nav-usuarios">Usuarios</MI>}
            {hasAccess("cabina_laboratorio") && (
              <Sub label="Ventas" icon={BadgeDollarSign} testid="nav-config-ventas">
                <MI to="/configuracion/ventas/laboratorio" icon={FlaskConical} testid="nav-cabina-laboratorio">Laboratorio</MI>
              </Sub>
            )}
            {(hasAccess("config_tablas_aeropuertos") || hasAccess("config_tablas_destinos") || hasAccess("config_tablas_aerolineas") || hasAccess("config_tablas_hoteles") || hasAccess("config_tablas_proveedores") || hasAccess("config_tablas_articulos") || hasAccess("config_tablas_centros") || hasAccess("config_tablas_empleados")) && (
              <Sub label="Tablas" icon={Table2} testid="nav-tablas">
                {hasAccess("config_tablas_aeropuertos") && <MI to="/configuracion/tablas/aeropuertos" icon={Plane} testid="nav-tablas-aeropuertos">Aeropuertos</MI>}
                {hasAccess("config_tablas_destinos") && <MI to="/configuracion/tablas/destinos" icon={MapPin} testid="nav-tablas-destinos">Destinos</MI>}
                {hasAccess("config_tablas_aerolineas") && <MI to="/configuracion/tablas/aerolineas" icon={Plane} testid="nav-tablas-aerolineas">Aerolíneas</MI>}
                {hasAccess("config_tablas_hoteles") && <MI to="/configuracion/tablas/hoteles" icon={Hotel} testid="nav-tablas-hoteles">Hoteles</MI>}
                {hasAccess("config_tablas_proveedores") && <MI to="/configuracion/tablas/proveedores" icon={Package} testid="nav-tablas-proveedores">Proveedores de Gastos</MI>}
                {hasAccess("config_tablas_articulos") && <MI to="/configuracion/tablas/articulos" icon={Package} testid="nav-tablas-articulos">Artículos</MI>}
                {hasAccess("config_tablas_centros") && <MI to="/configuracion/tablas/centros-trabajo" icon={Building2} testid="nav-tablas-centros">Centros de Trabajo</MI>}
                {hasAccess("config_tablas_empleados") && <MI to="/configuracion/tablas/empleados" icon={Users} testid="nav-tablas-empleados">Empleados</MI>}
                {hasAccess("config_tablas_departamentos") && <MI to="/configuracion/tablas/departamentos-operativos" icon={Building2} testid="nav-tablas-departamentos">Departamentos Operativos</MI>}
                {hasAccess("config_tablas_categorias_tkt") && <MI to="/configuracion/tablas/categorias-tickets" icon={Ticket} testid="nav-tablas-categorias-tkt">Categorías de Tickets</MI>}
              </Sub>
            )}
          </ModuleMenu>
        </Menubar>
        </div>
        </div>

        {/* Derecha: cotizador rápido + calculadora + tipo de cambio + usuario — compacto, pegado a la derecha */}
        <div className="flex items-center gap-1 shrink-0 ml-auto pr-0.5">
          <CalcQuick />
          <SimpleCalc />
          <QuickCalendar />
          {tipoCambio && (tipoCambio.usd || tipoCambio.eur) && (
            <div className="hidden lg:flex items-stretch gap-1 bg-white/10 rounded-lg px-1.5 py-0.5 border border-white/10 ml-0.5" data-testid="menubar-tc">
              {tipoCambio.usd && (
                <div className="flex flex-col items-center leading-none px-0.5">
                  <span className="text-[8.5px] font-bold tracking-wider text-cyan-200 uppercase">USD</span>
                  <span className="text-[11.5px] font-semibold text-white tabular-nums">${tipoCambio.usd.toFixed(4)}</span>
                </div>
              )}
              {tipoCambio.usd && tipoCambio.eur && <span className="w-px bg-cyan-300/30 my-1" />}
              {tipoCambio.eur && (
                <div className="flex flex-col items-center leading-none px-0.5">
                  <span className="text-[8.5px] font-bold tracking-wider text-cyan-200 uppercase">EUR</span>
                  <span className="text-[11.5px] font-semibold text-white tabular-nums">${tipoCambio.eur.toFixed(4)}</span>
                </div>
              )}
            </div>
          )}

          <Menubar className="border-0 bg-transparent h-auto p-0">
            <MenubarMenu>
              <MenubarTrigger className={`${triggerCls} px-1`} data-testid="user-panel-toggle">
                <span className="flex items-center gap-1.5">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full object-cover ring-2 ring-white/30" />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center"><User className="w-3.5 h-3.5 text-white" /></span>
                  )}
                  <span className="hidden md:block max-w-[90px] truncate text-white text-[11px] font-medium">{(user?.name || "Usuario").split(" ")[0]}</span>
                </span>
              </MenubarTrigger>
              <MenubarContent className={contentCls} align="end" sideOffset={8}>
                <div className="px-3 py-2">
                  <p className="text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  <Badge className={`mt-1.5 text-[10px] px-1.5 py-0 border-0 ${roleLabels[user?.role]?.color}`}>
                    <RoleIcon className="w-2.5 h-2.5 mr-0.5" /> {roleLabels[user?.role]?.label}
                  </Badge>
                </div>
                <MenubarSeparator className="bg-slate-400/25" />
                <MI to="/mi-perfil-viajero" icon={BookUser} testid="nav-mi-perfil-viajero">Mi Perfil de Viaje</MI>
                <MenubarSeparator className="bg-slate-400/25" />
                {gmailStatus.connected ? (
                  <MenubarItem className={itemCls} onSelect={disconnectGmail} data-testid="gmail-disconnect-btn">
                    <Mail className="w-4 h-4 text-emerald-500" />
                    <span className="flex-1 truncate">{gmailStatus.email || "Gmail conectado"}</span>
                    <span className="text-[10px] text-slate-400 underline">Desconectar</span>
                  </MenubarItem>
                ) : (
                  <MenubarItem className={itemCls} onSelect={connectGmail} data-testid="gmail-connect-btn">
                    <Mail className="w-4 h-4 text-slate-500" /> Conectar Gmail
                  </MenubarItem>
                )}
                <MenubarSeparator className="bg-slate-400/25" />
                <MenubarItem className={`${itemCls} text-red-600 focus:bg-red-500 focus:text-white`} onSelect={handleLogout} data-testid="logout-button">
                  <LogOut className="w-4 h-4" /> Cerrar Sesión
                </MenubarItem>
              </MenubarContent>
            </MenubarMenu>
          </Menubar>
        </div>
      </div>

      {/* Menú móvil (hamburguesa) */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-80 p-0 overflow-y-auto border-r" data-testid="mobile-nav-sheet">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 sticky top-0 z-10 bg-white">
            {settings?.sidebar_logo_url ? (
              <img src={settings.sidebar_logo_url} alt="Logo" className="h-7 object-contain" />
            ) : (
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: "#04ccda" }}><Plane className="w-4 h-4 text-white" /></div>
            )}
            <div className="min-w-0">
              <p className="font-bold text-sm text-slate-800 truncate">{settings?.name || "PD Travel"}</p>
              <p className="text-[11px] text-slate-500 truncate">{user?.name}</p>
            </div>
          </div>
          <nav className="p-3 pb-6 space-y-4">
            <button onClick={() => goMobile("/")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[14px] font-semibold text-slate-800 hover:bg-blue-50" data-testid="mobile-nav-inicio">
              <Home className="w-4 h-4 text-slate-500" /> Inicio
            </button>
            {hasAccess("comercial_dashboard") && (
              <button onClick={() => goMobile("/dashboard-comercial")} className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] font-medium ${location.pathname === "/dashboard-comercial" ? "bg-[#0037ff] text-white" : "text-slate-700 hover:bg-blue-50"}`} data-testid="mobile-nav-dashboard-comercial">
                <TrendingUp className={`w-4 h-4 ${location.pathname === "/dashboard-comercial" ? "text-white" : "text-slate-500"}`} /> Dashboard
              </button>
            )}
            {seccionesMobile.map((sec) => (
              <div key={sec.label}>
                <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{sec.label}</p>
                {!hasSectionAccess(sec.key) ? (
                  <p className="px-3 py-1.5 text-[12px] text-slate-400 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> No tienes acceso a este módulo</p>
                ) : sec.items.map((it) => (
                  <button key={it.to + it.label} onClick={() => goMobile(it.to)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] font-medium text-left ${location.pathname === it.to ? "bg-[#0037ff] text-white" : "text-slate-700 hover:bg-blue-50"}`}
                    data-testid={`mobile-${sec.key}-${it.label.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                    <it.icon className={`w-4 h-4 ${location.pathname === it.to ? "text-white" : "text-slate-500"}`} /> {it.label}
                  </button>
                ))}
              </div>
            ))}
            <div className="pt-2 border-t border-slate-300/40">
              <button onClick={() => goMobile("/mi-perfil-viajero")} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] font-medium text-slate-700 hover:bg-blue-50" data-testid="mobile-nav-mi-perfil">
                <BookUser className="w-4 h-4 text-slate-500" /> Mi Perfil de Viaje
              </button>
              <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[13.5px] font-medium text-red-600 hover:bg-red-50" data-testid="mobile-logout">
                <LogOut className="w-4 h-4" /> Cerrar Sesión
              </button>
            </div>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
};

export default TopMenuBar;
